import React, { useState, useEffect } from 'react';
import { FaTimes, FaEdit, FaUsers, FaMoneyBill, FaFileContract, FaExclamationTriangle, FaFileInvoiceDollar, FaCogs, FaProjectDiagram } from 'react-icons/fa';
import Button from '../../../../components/common/Button';
import colors from '../../../../styles/colors.css';

const ChangeGLModal = ({ isOpen, onClose, onSubmit, requisition, allUsers, currentUser, originalUser, isMasquerading }) => {
  const [formData, setFormData] = useState({
    gl_code: '',
    gl_description: '',
    current_mid_approver: '',
    current_second_approver: '',
    current_sap_approver: '',
    previous_mid_approver: '',
    previous_second_approver: '',
    previous_sap_approver: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Extract users array from the response structure
    const usersArray = allUsers?.data?.rows || allUsers?.rows || (Array.isArray(allUsers) ? allUsers : []);
    
    if (isOpen && requisition && usersArray && Array.isArray(usersArray)) {
      // Debug logging
      console.log('🔍 ChangeGLModal Debug:', {
        requisition_mid: requisition.mid,
        requisition_second: requisition.second,
        requisition_approval: requisition.approval,
        requisition_mid_type: typeof requisition.mid,
        requisition_second_type: typeof requisition.second,
        requisition_approval_type: typeof requisition.approval,
        usersArray_count: usersArray.length,
        usersArray_sample: usersArray.slice(0, 2).map(u => ({ id: u.user_id, name: u.user_full_name, id_type: typeof u.user_id })),
        // CRITICAL: Check if the required users are in usersArray
        has_user_64: usersArray.find(u => String(u.user_id) === "64"),
        has_user_86: usersArray.find(u => String(u.user_id) === "86"),
        has_user_93: usersArray.find(u => String(u.user_id) === "93"),
        usersArray_all_ids: usersArray.map(u => u.user_id)
      });
      
      const newFormData = {
        gl_code: requisition.gl_code || '',
        gl_description: requisition.description || '',
        current_mid_approver: String(requisition.mid || ''),
        current_second_approver: String(requisition.second || ''),
        current_sap_approver: String(requisition.approval || ''),
        previous_mid_approver: requisition.mid_manager?.user_full_name || '',
        previous_second_approver: requisition.second_approval?.user_full_name || '',
        previous_sap_approver: requisition.sap_approval?.user_full_name || ''
      };
      
      console.log('🔍 Setting form data:', newFormData);
      setFormData(newFormData);
    } else {
      console.error('🚨 ChangeGLModal: Invalid props:', {
        isOpen,
        requisition: !!requisition,
        allUsers: allUsers,
        allUsers_type: typeof allUsers,
        usersArray_isArray: Array.isArray(usersArray),
        // Let's see what's inside allUsers object
        allUsers_keys: allUsers ? Object.keys(allUsers) : null,
        allUsers_values: allUsers ? Object.values(allUsers).slice(0, 2) : null
      });
    }
  }, [isOpen, requisition, allUsers]);

  if (!isOpen) return null;

  // Extract users array for use in JSX
  const usersArray = allUsers?.data?.rows || allUsers?.rows || (Array.isArray(allUsers) ? allUsers : []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error updating GL information:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-2 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col">
        {/* Compact Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaCogs className="text-white text-lg mr-2" />
              <div>
                <h2 className="text-lg font-bold text-white">GL Information</h2>
                <p className="text-blue-100 text-xs">Update approval workflow</p>
              </div>
            </div>
            <div className="flex items-center">
              <FaProjectDiagram className="text-white text-lg mr-3 opacity-80" />
              <button
                onClick={onClose}
                disabled={loading}
                className="w-6 h-6 bg-white bg-opacity-20 rounded flex items-center justify-center text-white hover:bg-opacity-30"
              >
                <FaTimes size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Compact Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-3 space-y-3">
            {/* GL Code - Inline */}
            <div className="flex items-center gap-3">
              <FaMoneyBill className="text-green-600 text-sm flex-shrink-0" />
              <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">GL Code</label>
              <input
                type="text"
                value={formData.gl_code}
                readOnly
                className="flex-1 px-2 py-1 border border-gray-200 rounded bg-gray-50 text-gray-700 font-mono text-sm cursor-not-allowed"
              />
            </div>

            {/* Description - Inline */}
            <div className="flex items-center gap-3">
              <FaFileContract className="text-blue-500 text-sm flex-shrink-0" />
              <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">Description</label>
              <input
                type="text"
                name="gl_description"
                value={formData.gl_description}
                onChange={handleInputChange}
                className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-sm"
                placeholder="Enter description..."
                required
              />
            </div>

            {/* Compact Approvers */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <FaUsers className="text-gray-600 text-sm" />
                Approval Hierarchy
              </h3>

              {/* First Approver - Compact */}
              <div className="flex items-center gap-3">
                <span className="text-orange-500 text-sm">🥇</span>
                <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">First</label>
                <select
                  name="current_mid_approver"
                  value={formData.current_mid_approver}
                  onChange={handleInputChange}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-green-500"
                  required
                >
                  <option value="">Select First Approver</option>
                  {/* Show current approver even if not in usersArray list */}
                  {formData.current_mid_approver && Array.isArray(usersArray) && !usersArray.find(u => String(u.user_id) === formData.current_mid_approver) && (
                    <option key={`current-${formData.current_mid_approver}`} value={formData.current_mid_approver} selected={true}>
                      {formData.previous_mid_approver || `User ID ${formData.current_mid_approver}`}
                    </option>
                  )}
                  {Array.isArray(usersArray) && usersArray.map(user => {
                    const isSelected = String(user.user_id) === formData.current_mid_approver;
                    console.log(`First Approver - User ${user.user_id} (${user.user_full_name}): ${isSelected ? 'SELECTED' : 'not selected'} - comparing ${String(user.user_id)} === ${formData.current_mid_approver}`);
                    return (
                      <option key={user.user_id} value={String(user.user_id)} selected={isSelected}>
                        {user.user_full_name} ({user.role_name})
                      </option>
                    );
                  })}
                  {!Array.isArray(usersArray) && (
                    <option value="" disabled>No users available</option>
                  )}
                </select>
              </div>

              {/* Second Approver - Compact */}
              <div className="flex items-center gap-3">
                <span className="text-blue-500 text-sm">🥈</span>
                <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">Second</label>
                <select
                  name="current_second_approver"
                  value={formData.current_second_approver}
                  onChange={handleInputChange}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-green-500"
                  required
                >
                  <option value="">Select Second Approver</option>
                  {/* Show current approver even if not in usersArray list */}
                  {formData.current_second_approver && Array.isArray(usersArray) && !usersArray.find(u => String(u.user_id) === formData.current_second_approver) && (
                    <option key={`current-${formData.current_second_approver}`} value={formData.current_second_approver} selected={true}>
                      {formData.previous_second_approver || `User ID ${formData.current_second_approver}`}
                    </option>
                  )}
                  {Array.isArray(usersArray) && usersArray.map(user => {
                    const isSelected = String(user.user_id) === formData.current_second_approver;
                    console.log(`Second Approver - User ${user.user_id} (${user.user_full_name}): ${isSelected ? 'SELECTED' : 'not selected'} - comparing ${String(user.user_id)} === ${formData.current_second_approver}`);
                    return (
                      <option key={user.user_id} value={String(user.user_id)} selected={isSelected}>
                        {user.user_full_name} ({user.role_name})
                      </option>
                    );
                  })}
                  {!Array.isArray(usersArray) && (
                    <option value="" disabled>No users available</option>
                  )}
                </select>
              </div>

              {/* SAP Approver - Compact */}
              <div className="flex items-center gap-3">
                <span className="text-purple-500 text-sm">🏆</span>
                <label className="text-sm font-medium text-gray-700 w-20 flex-shrink-0">SAP</label>
                <select
                  name="current_sap_approver"
                  value={formData.current_sap_approver}
                  onChange={handleInputChange}
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-green-500"
                  required
                >
                  <option value="">Select SAP Approver</option>
                  {/* Show current approver even if not in usersArray list */}
                  {formData.current_sap_approver && Array.isArray(usersArray) && !usersArray.find(u => String(u.user_id) === formData.current_sap_approver) && (
                    <option key={`current-${formData.current_sap_approver}`} value={formData.current_sap_approver} selected={true}>
                      {formData.previous_sap_approver || `User ID ${formData.current_sap_approver}`}
                    </option>
                  )}
                  {Array.isArray(usersArray) && usersArray.map(user => {
                    const isSelected = String(user.user_id) === formData.current_sap_approver;
                    console.log(`SAP Approver - User ${user.user_id} (${user.user_full_name}): ${isSelected ? 'SELECTED' : 'not selected'} - comparing ${String(user.user_id)} === ${formData.current_sap_approver}`);
                    return (
                      <option key={user.user_id} value={String(user.user_id)} selected={isSelected}>
                        {user.user_full_name} ({user.role_name})
                      </option>
                    );
                  })}
                  {!Array.isArray(usersArray) && (
                    <option value="" disabled>No users available</option>
                  )}
                </select>
              </div>
            </div>

            {/* Compact Warning */}
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded p-2">
              <div className="flex items-start">
                <FaExclamationTriangle className="text-amber-500 mr-2 mt-0.5 text-sm flex-shrink-0" />
                <div>
                  <h4 className="text-xs font-semibold text-amber-800 mb-1">Important Notice</h4>
                  <p className="text-xs text-amber-700">
                    Changes will update approval workflow and be logged for audit.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Compact Footer */}
          <div className="bg-gray-50 px-3 py-2 rounded-b-lg border-t border-gray-200">
            <div className="flex justify-end gap-2">
              <Button 
                variant="secondary" 
                onClick={onClose}
                disabled={loading}
                className="px-4 py-1.5 text-sm"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
                icon={loading ? null : FaEdit}
                className="px-4 py-1.5 text-sm bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                    Updating...
                  </div>
                ) : (
                  'Update GL'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeGLModal;
